import { useState, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { Link, useNavigate } from 'react-router-dom';
import moment from 'moment';

import Checkbox from "@mui/joy/Checkbox";
import Grid from "@mui/joy/Grid";
import Stack from "@mui/joy/Stack";
import Typography from "@mui/joy/Typography";
import ListItemButton from "@mui/joy/ListItemButton";
import Button from "@mui/joy/Button";
import { Table } from '@mui/joy';
import IconButton from '@mui/joy/IconButton';
import Sheet from '@mui/joy/Sheet';
import Input from '@mui/joy/Input';

import { useSortable as useDNDKitSortable } from '@dnd-kit/sortable';

import { CollabProps } from 'stores/collab_store';
import { FHRequestType } from 'stores/fhrequest_store';
import { IdentityProps } from 'stores/identity_store';

import ActiveCollaboratorsChip from 'components/ActiveCollaboratorsChip';
import IdentityAvatar from 'components/IdentityAvatar';
import useUserIdentity from 'hooks/useUserIdentity';

import ClockInOutWidget from 'components/ClockInOutWidget';

import useCollabs from 'hooks/useCollabs';
import useClock from 'hooks/useClock';
import { DateOrTime, TimeUI, Duration } from './Date';
import useCurrentIdentity from 'hooks/useCurrentIdentity';
import { ClockProps } from 'stores/clock_store';

import getIcon from 'utils/getIcon';
import useRoles from 'hooks/useRoles';
import useWorkKey from 'hooks/useWorkKey';

import { ClockSupportForm } from './CreditLog';
import { datetimeLocalFormat } from 'utils/misc';
import { timeDuration } from 'utils/misc';

import Success from 'components/Success';



export const WORK_ITEM_FONT_SIZE = 'var(--joy-fontSize-xl)';

function work_item_url(item: CollabProps | FHRequestType, identity: IdentityProps | null) {


    if (item.cls === 'request')
        return `/request/${item.uuid}`;
    if (item.variant === 'support')
        return `/support/${item.uuid}`;

    if (identity?.role == 'assistant')
        return `/collab/${item.uuid}`;
    else
        return `/project/${item.project}/collab/${item.uuid}`;
}

type WorkItemType = CollabProps | FHRequestType;

const REQUIRED_CLOCK_EDIT_ROLE = 'support';

export const ListicleWorkItemCheckbox = observer(({ item, onCheck }: { item: WorkItemType, onCheck: (i: WorkItemType, b: boolean) => void }) => {
    const [checked, setChecked] = useState(item.completed);

    const {
        attributes,
        listeners,
    } = useDNDKitSortable({ id: item.uuid });

    const handleCheck = (value: boolean) => {
        onCheck && onCheck(item, value);
        setChecked(value);
    };

    return (
        <Grid container spacing={1}
            alignItems="flex-start">
            <Grid
                pt={1}
                xs={1} sx={{ maxWidth: 32 }}
            >
                <Stack
                    className="dnd-knob"
                    {...attributes} {...listeners}
                    sx={{
                        width: 32,
                        height: 32,
                        alignItems: 'center',
                        justifyContent: 'center',
                        justifyItems: 'center',
                    }}>
                    <Checkbox
                        checked={checked}
                        onChange={() => handleCheck(!checked)}
                    />
                </Stack>

            </Grid>
            <Grid xs={11}>
                <ListicleWorkItem
                    key={item.key}
                    item={item} />
            </Grid>
        </Grid>

    );
});

const ListicleWorkItemCollabortorsForRequest = observer(({ item }: { item: FHRequestType }) => {
    if (!item.claimed_identity) return null;
    return (
        <Stack>
            <IdentityAvatar
                width={16} height={16}
                size="inline" identityKey={item.claimed_identity} />
        </Stack>
    );
});


// const ListicleWorkItemCollabortorsForCollab = observer(({ item }: { item: CollabProps }) => {
//     const { getCollaborators, fetchListForCollab } = useCollaborators();
//     const { currentIdentity } = useUserIdentity();
//     const [loaded, setLoaded] = useState(false);
//     // All collaborators except yourself.
//     const cbs = getCollaborators(item.uuid).filter((c) => c.identity != currentIdentity?.uuid);
//     useEffect(() => {
//         if(!currentIdentity.uuid) return;
//         if(loaded) return;
//         setLoaded(true); // close enough
//         fetchListForCollab(currentIdentity.uuid, item.uuid);
//     }, [currentIdentity.uuid, item.uuid]);
//     return (
//         <Stack direction="row" spacing={1}>
//             { cbs.length == 1 &&
//                 <IdentityAvatar
//                     interactive={false}
//                     size="inline"
//                     width={16} height={16}
//                     identityKey={cbs[0].identity} /> }
//         </Stack>
//     );
// });

// const ListicleWorkItemCollaborators = observer(({ item }: { item: CollabProps | FHRequestType }) => {

//     if(item.cls == 'request') {
//         return <ListicleWorkItemCollabortorsForRequest item={item as FHRequestType} />;
//     }
//     else {
//         return <ListicleWorkItemCollabortorsForCollab item={item as CollabProps} />;
//     }
// });

const ListicleWorkItemLastActionForCollab = observer(({ item }: { item: CollabProps }) => {
    const { getCollab } = useCollabs();
    const { currentIdentity } = useUserIdentity();
    const collab = getCollab(item.uuid);


    // If we don't have anythign meaningful to show, show the person who created it
    if (!collab?.last_action_summary || !collab?.last_action_identity) {

        // unless the person who created it is looking at it.
        if (currentIdentity.uuid == collab?.identity)
            return null;

        return <IdentityAvatar
            interactive={false}
            size="inline"
            width={16}
            height={16}
            identityKey={collab?.identity} />;
    }

    return (
        <Stack>

            <Typography level="body-sm">
                {collab.last_action_summary}
            </Typography>

            <Stack direction="row"
                spacing={1}
                alignItems="center"
            >

                {item.last_action_identity &&
                    <Typography level="body-xs">
                        <IdentityAvatar
                            interactive={false}
                            size="inline"
                            width={16} height={16}
                            identityKey={item.last_action_identity} />
                    </Typography>}
                {item.last_action_date &&
                    <Typography level="body-xs">
                        <DateOrTime ts={item.last_action_date} />
                    </Typography>}


            </Stack>
        </Stack>
    );
});

export const ListicleWorkItemLastAction = observer(({ item }: { item: CollabProps | FHRequestType }) => {
    if (item.cls == 'request') {
        return <ListicleWorkItemCollabortorsForRequest item={item as FHRequestType} />;
    }
    else {
        return <ListicleWorkItemLastActionForCollab item={item as CollabProps} />
    }
});

const ListicleWorkItem = observer(({ item }: { item: CollabProps | FHRequestType }) => {
    const { currentIdentity } = useUserIdentity();
    const navigate = useNavigate();
    const url = work_item_url(item, currentIdentity);
    const [showClocks, setShowClocks] = useState(false);

    return (
        <Stack>
        <Stack direction="row" spacing={1}
            justifyContent="space-between"
            alignItems="center">


            <ListItemButton
                onClick={() => navigate(url)}

                sx={{
                    borderRadius: 'md',
                    flexGrow: 1,
                    py: 0.5,
                    px: 1
                }}>

                <Stack

                >
                    <Stack
                        direction="row"
                        pr={1}
                        spacing={1}
                        width="100%"
                        justifyContent="space-between"
                    >
                        <Link to={url}>
                            <Typography
                                sx={{
                                    fontSize: WORK_ITEM_FONT_SIZE,
                                    opacity: item.completed ? 0.3 : 1,
                                    transition: 'all 0.3s ease-in-out'
                                }}
                                level="title-md">{
                                    item?.title || "Untitled Collab"
                                }</Typography>
                        </Link>
                        <ActiveCollaboratorsChip
                            size="sm"
                            associated_type={item.cls}
                            associated_uuid={item.uuid}
                        />

                    </Stack>

                    {/* footer */}
                    <Stack>
                        <Typography
                            sx={{
                                opacity: 0.6,
                                transition: 'all 0.3s ease-in-out'
                            }}
                            level="body-xs">
                            <ListicleWorkItemLastAction item={item} />
                        </Typography>
                    </Stack>

                </Stack>

            </ListItemButton>


            {(item.cls == 'collab' && currentIdentity.role == 'assistant') &&
                <Button variant="plain" onClick={() => setShowClocks(!showClocks)}>
                <Typography level="body-xs">
                                                Clocks
                                                                                   </Typography>
                </Button>
            }
            {(item.cls == 'collab') &&
                <ClockInOutWidget collab={item as CollabProps} />
            }
            </Stack>
            { showClocks && item &&
                <ListicleClockExpanded collab={item as CollabProps} />
            }
            </Stack>
    );
});

const ClockRow = observer(({ clock }: { clock: ClockProps }) => {
    const [open, setOpen] = useState(false);
    const { hasRole } = useRoles();

    const KeyboardArrowUpIcon = getIcon('KeyboardArrowUp');

    return (
        <>
        <tr>
                <td><IdentityAvatar identityKey={clock.identity} size="inline" /></td>
                <td><TimeUI ts={clock.time_start} /></td>
                <td><TimeUI ts={clock.time_end} /></td>
                <td><Duration start={clock.time_start} end={clock.time_end} /></td>
                { (hasRole(REQUIRED_CLOCK_EDIT_ROLE) && clock.time_end) ?
            <td>
                <IconButton
                    aria-label="expand row"
                    variant="plain"
                    color="neutral"
                    size="sm"
                    onClick={() => setOpen(!open)}
            >
                            <Typography
                                endDecorator={<KeyboardArrowUpIcon
                                    sx={{
                                        transition: 'all 0.3 ease-in-out',
                                        transform: !open ? 'rotate(180deg)' : 'rotate(0deg)'
                                    }}
                                />}>
                                {open ? 'Hide' : 'Edit'}
                            </Typography>

                    </IconButton>
                    </td>
                    :  <td>Cannot edit live clocks</td>
                }
                </tr>

                {open && hasRole(REQUIRED_CLOCK_EDIT_ROLE) &&
                <tr>
                    <td colSpan={5}>

                        <Sheet
                            variant="soft"
                            sx={{ p: 1, pl: 6, boxShadow: 'inset 0 3px 6px 0 rgba(0 0 0 / 0.08)' }}
                        >
                            <Typography level="body-lg" component="div">
                                History
                            </Typography>

                            <ClockSupportForm clock={clock} />

                        </Sheet>
                    </td>
                </tr>}
        </>
    )
});


const NewClockForm  = observer(({ collab }: { collab: CollabProps }) => {

    const [showing, setShowing] = useState(false);
    const { hasRole } = useRoles();
    const { createManualClock } = useClock();
    const workKey = useWorkKey();

    const [timeStart, setTimeStart] = useState(datetimeLocalFormat(new Date()));
    const [timeEnd, setTimeEnd] = useState(datetimeLocalFormat(new Date()));
    const [originalDuration, setOriginalDuration] = useState(0);
    const [diff, setDiff] = useState(0);
    const [invalid, setInvalid] = useState(false);

    const AddIcon = getIcon('Add');

    const MIN_CLOCK_DURATION = 60;
    const MAX_CLOCK_DURATION = 60 * 60 * 4;
    useEffect(() => {
        const start = new Date(timeStart);
        const end = new Date(timeEnd);
        const diff = end.getTime() - start.getTime();
        setDiff(diff / 1_000);

        if ((diff / 1_000) < MIN_CLOCK_DURATION) {
            console.log("invalid", diff);
            setInvalid(true);
        }
        else {
            setInvalid(false);
        }
    }, [timeStart, timeEnd, setDiff, setInvalid, setOriginalDuration]);


    function doSave() {
        if (!workKey) return;
        createManualClock(
            workKey,
            collab.uuid,
            moment(timeStart).utc().format(),
            moment(timeEnd).utc().format()
        )
            .then((res) => {
                if (!res) return;
                Success('Saved!');
                setShowing(false);
            });
    }

    if(!hasRole('support')) return null;

    return (

        <Stack m={1}>

        <Button
            onClick={() => setShowing(!showing)}
            variant="plain"
                         color="neutral"
                         endDecorator={<AddIcon sx={{
                             transition: 'all 0.3s ease-in-out',
                             transform: showing ? 'rotate(45deg)' : 'rotate(0deg)'
                         }}/>}
                         size="sm">{ showing ? 'Hide' : 'Add a new clock'}</Button>
        { showing &&
            <Stack spacing={1} m={1}>

            <Stack spacing={1}>

                <Stack spacing={1} direction={{ xs: 'column', sm: 'row' }} >
                    <Stack>
                        <Typography level="body-xs">
                            Start
                        </Typography>
                        <Input
                            onChange={(e) => setTimeStart(e.target.value)}
                            type="datetime-local" value={timeStart} />
                    </Stack>

                    <Stack>
                        <Typography level="body-xs">
                            End
                        </Typography>
                        <Input
                            onChange={(e) => setTimeEnd(e.target.value)}
                            type="datetime-local" value={timeEnd} />
                    </Stack>
                </Stack>

                {                 (diff >= MIN_CLOCK_DURATION) &&
                <Stack>
                    <Typography level="body-xs">
                        Duration
                    </Typography>

                    <Stack direction="row" spacing={1} alignItems="stretch" >


                        <Typography
                            p={1}
                            color={invalid ? 'danger' : 'success'}
                            level="body-sm"
                            variant="soft">
                            Duration: {timeDuration(diff)}
                        </Typography>
                    </Stack>

                    </Stack>
                }

                {Math.abs(Math.floor(diff - originalDuration)) > 60 &&
                    <Stack>
                        <Typography level="body-xs">
                            Expected Change
                        </Typography>

                        <Typography
                            p={1}
                            color={(diff - originalDuration) ? 'danger' : 'success'}
                            level="body-sm"
                        >
                            {(diff - originalDuration) ? "Add" : "Remove"} {' '}
                            {timeDuration(diff - originalDuration)}
                        </Typography>
                    </Stack>
                }
            </Stack>

            {
                (diff < MIN_CLOCK_DURATION) &&
                <Typography level="body-xs" color="danger">
                    The duration is less than {MIN_CLOCK_DURATION / 60} minute.
                </Typography>
            }

            {
                (diff > MAX_CLOCK_DURATION) &&
                <Typography level="body-xs" color="danger">
                    The duration is longer than {MAX_CLOCK_DURATION / 60} minutes.
                </Typography>
            }

            {
                Math.abs(Math.floor(diff - originalDuration)) > 60 && !invalid &&
                <Button onClick={doSave}>Save</Button>
            }

            </Stack >
            }
            </Stack>
    );
});

const ListicleClockExpanded = observer(({ collab }: { collab: CollabProps }) => {
    const { hasRole } = useRoles();
    const {
        fetchClocksForCollab, getClocksForCollab,
    } = useClock();

    const currentIdentity = useCurrentIdentity();
    const clocks = getClocksForCollab(collab.uuid);
    useEffect(() => {
        fetchClocksForCollab(collab.uuid, currentIdentity.uuid);
    }, [currentIdentity?.uuid]);
    return (
        <Stack>
        <NewClockForm collab={collab} />

            <Table>
                <thead>
                    <tr>
                        <th>Assistant</th>
                        <th>Start</th>
                        <th>End</th>
                        <th>Duration</th>
                        {hasRole(REQUIRED_CLOCK_EDIT_ROLE) &&
                            <th>Edit</th>
                        }

                    </tr>
                </thead>
                <tbody>
                    {clocks.map((c) =>
                        <ClockRow clock={c} />)}
                </tbody>
            </Table>

        </Stack>
    );
});

export default ListicleWorkItem;
